import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Radio,
  Select,
  Spin,
  Switch,
  Tag,
  message as antdMessage,
} from "antd";
import { BellOutlined, FolderOpenOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { FontSizeOutlined } from "@ant-design/icons";
import { useAuth } from "../hooks/useAuth";
import { useColorMode } from "../theme/ThemeProvider";
import type { FontScale } from "../theme/tokens";
import { PageHeader } from "../components/PageHeader";
import { api } from "../api/client";
import { getMeSettings, updateMeSettings } from "../api/me";
import type { MeSettings } from "../api/me";
import { useWebPush } from "../hooks/useWebPush";
import { FollowupTemplatesCard } from "../components/FollowupTemplatesCard";
import { EmailTemplatesCard } from "../components/EmailTemplatesCard";

type FormValues = {
  aiCoreIdea: string;
  aiMode: "preview" | "auto";
};

export function SettingsPage() {
  const { user } = useAuth();
  const { mode, fontScale, setFontScale } = useColorMode();
  const [form] = Form.useForm<FormValues>();
  const [settings, setSettings] = useState<MeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [llmConfigured, setLlmConfigured] = useState(false);
  const { state: pushState, enable: enablePush, disable: disablePush } = useWebPush();

  useEffect(() => {
    getMeSettings()
      .then((s) => {
        setSettings(s);
        form.setFieldsValue({
          aiCoreIdea: s.aiCoreIdea,
          aiMode: s.aiMode,
        });
      })
      .finally(() => setLoading(false));
    // ai status: lightweight call to surface "API 未配置" hint
    api<{ configured?: boolean }>("/ai/status")
      .then((d) => setLlmConfigured(Boolean(d?.configured)))
      .catch(() => null);
  }, [form]);

  const onSave = async (values: FormValues) => {
    setSaving(true);
    try {
      const updated = await updateMeSettings(values);
      setSettings(updated);
      antdMessage.success("已保存");
    } catch {
      antdMessage.error("保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="设置" subtitle="账号、AI、跟进模板、通知偏好。" />
      <div style={{ padding: 24, display: "grid", gap: 16, maxWidth: 720, margin: "0 auto" }}>
        <Card title="账号">
          <div style={{ display: "grid", gap: 8 }}>
            <Row label="姓名">{user?.name ?? "—"}</Row>
            <Row label="邮箱">{user?.email ?? "—"}</Row>
            <Row label="主题">
              <Tag>{mode === "dark" ? "深色" : "浅色"}</Tag>
            </Row>
          </div>
        </Card>

        <Card
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FontSizeOutlined /> 字体大小
            </span>
          }
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
              整个 App 的字号会按这个倍率即时缩放,设置保存在本地浏览器。
            </div>
            <Radio.Group
              value={fontScale}
              onChange={(e) => setFontScale(e.target.value as FontScale)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: "小", value: "sm" },
                { label: "中", value: "md" },
                { label: "大", value: "lg" },
                { label: "超大", value: "xl" },
              ]}
            />
          </div>
        </Card>

        <Card
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <BellOutlined /> 浏览器推送通知
            </span>
          }
          extra={
            !pushState.supported ? (
              <Tag>浏览器不支持</Tag>
            ) : !pushState.configured ? (
              <Tag color="warning">服务端未配置</Tag>
            ) : pushState.subscribed ? (
              <Tag color="green">已订阅</Tag>
            ) : (
              <Tag>未订阅</Tag>
            )
          }
        >
          {!pushState.supported ? (
            <Alert
              type="info"
              showIcon
              message="当前浏览器不支持 Web Push"
              description="iOS Safari 需要先把页面「添加到主屏幕」作为 PWA 后才能开启推送。"
              style={{ borderRadius: 10 }}
            />
          ) : !pushState.configured ? (
            <Alert
              type="warning"
              showIcon
              message="VAPID 密钥未配置"
              description={
                <span>
                  后端跑 <code>pnpm gen-vapid</code> 拿到一对 key,粘到 <code>backend/.env</code> 然后重启。
                </span>
              }
              style={{ borderRadius: 10 }}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 15, color: "var(--ant-color-text-secondary)", flex: 1 }}>
                打开后,重要联系人的来信在标签页关闭/最小化时也能弹出系统通知。
              </div>
              <Switch
                checked={pushState.subscribed}
                loading={pushState.busy}
                onChange={(c) => (c ? enablePush() : disablePush())}
              />
            </div>
          )}
        </Card>

        <Card
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <ThunderboltOutlined /> AI 自动回复
            </span>
          }
          extra={
            llmConfigured ? (
              <Tag color="green">API 已配置</Tag>
            ) : (
              <Tag color="warning">API 未配置</Tag>
            )
          }
        >
          {!llmConfigured ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, borderRadius: 10 }}
              message="LLM 凭据未配置"
              description={
                <span>
                  在 <code>backend/.env</code> 填 <code>AZURE_OPENAI_*</code> 或 <code>OPENAI_API_KEY</code>,重启后端即可启用。
                </span>
              }
            />
          ) : null}

          {loading ? (
            <Spin />
          ) : (
            <Form<FormValues>
              form={form}
              layout="vertical"
              onFinish={onSave}
              initialValues={{ aiCoreIdea: "", aiMode: "preview" }}
            >
              <Form.Item
                name="aiCoreIdea"
                label="回复中心思想"
                extra="一段中心思想 / 默认话术,AI 会基于它给「不重要联系人」起草回复。"
              >
                <Input.TextArea
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  placeholder="例如:礼貌确认收到,告知本周不便深入讨论,下周再约时间。语气自然简洁,不做任何承诺。"
                  maxLength={2000}
                  showCount
                  disabled={!llmConfigured}
                />
              </Form.Item>
              <Form.Item name="aiMode" label="工作模式">
                <Radio.Group disabled={!llmConfigured}>
                  <Radio.Button value="preview">预览审核</Radio.Button>
                  <Radio.Button value="auto">全自动</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)", marginBottom: 16 }}>
                <strong>预览审核</strong>:AI 起草后等你点 Approve 才发送。<br />
                <strong>全自动</strong>:AI 起草后立即发送(只对「不重要联系人」生效)。
              </div>

              <Button type="primary" htmlType="submit" loading={saving}>
                保存
              </Button>
            </Form>
          )}
        </Card>

        <CaseTriggerCard settings={settings} loading={loading} onSaved={setSettings} />

        <EmailTemplatesCard />

        <FollowupTemplatesCard />
      </div>
    </>
  );
}

function CaseTriggerCard({
  settings,
  loading,
  onSaved,
}: {
  settings: MeSettings | null;
  loading: boolean;
  onSaved: (s: MeSettings) => void;
}) {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    const parsed = settings.caseTrigger
      .split(/[,，]/)
      .map((s) => s.trim())
      .filter(Boolean);
    setKeywords(parsed);
  }, [settings]);

  const onSave = async () => {
    setSaving(true);
    try {
      const trimmed = keywords.map((k) => k.trim()).filter(Boolean);
      const updated = await updateMeSettings({ caseTrigger: trimmed.join(",") });
      onSaved(updated);
      antdMessage.success("已保存");
    } catch {
      antdMessage.error("保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FolderOpenOutlined /> 案件触发词
        </span>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
            把邮件转发到自己的邮箱,正文或主题里带上任一关键词,系统会自动新建案件、归档邮件与附件,并用 AI 生成摘要。
            可加多个关键词,任一命中即触发。
          </div>
          <Select
            mode="tags"
            value={keywords}
            onChange={(v) => setKeywords(v as string[])}
            placeholder="输入关键词后回车,例如:boss-brain"
            tokenSeparators={[",", "，"]}
            style={{ maxWidth: 520 }}
            maxTagCount={20}
          />
          <div style={{ fontSize: 13, color: "var(--ant-color-text-secondary)" }}>
            匹配不区分大小写,会同时在邮件主题与正文里查找。
          </div>
          <div>
            <Button
              type="primary"
              loading={saving}
              disabled={keywords.length === 0}
              onClick={onSave}
            >
              保存
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 16 }}>
      <span style={{ width: 80, color: "var(--ant-color-text-secondary)", fontSize: 15 }}>{label}</span>
      <span style={{ fontSize: 15 }}>{children}</span>
    </div>
  );
}
